import { useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import Icon from '../../components/Icon';
import { toast } from '../../components/Toast';
import { commandCenterApi } from '../../utils/api';
import mermaidRedhead from '../../assets/peak-mermaid-redhead.svg';

// The daily message (server/lib/ccDailyMessage.js): the mermaid note its one
// recipient sees once a day, and the editor for its text. Who gets it and
// whether it is on are the server's business; nothing here decides either.

// Fixed scatter rather than Math.random(): a re-render must not move anything.
// The negative delays start each one part-way up, so the water is already full
// when the modal opens. With reduced motion they simply stay where they are.
const BUBBLES = [
  { x: '6%', y: '14%', size: 14, delay: '-1s', duration: '7s' },
  { x: '16%', y: '46%', size: 26, delay: '-4s', duration: '9s' },
  { x: '28%', y: '6%', size: 10, delay: '-2s', duration: '6s' },
  { x: '68%', y: '10%', size: 20, delay: '-6s', duration: '8s' },
  { x: '80%', y: '42%', size: 12, delay: '-3s', duration: '6.5s' },
  { x: '90%', y: '20%', size: 24, delay: '-5s', duration: '9.5s' },
];
const HEARTS = [
  { x: '10%', y: '28%', size: 24, delay: '-2s', duration: '8s' },
  { x: '24%', y: '12%', size: 18, delay: '-5s', duration: '7s' },
  { x: '32%', y: '30%', size: 16, delay: '-7s', duration: '9s' },
  { x: '62%', y: '6%', size: 20, delay: '-3s', duration: '7.5s' },
  { x: '72%', y: '24%', size: 26, delay: '-1s', duration: '8.5s' },
  { x: '86%', y: '8%', size: 18, delay: '-4s', duration: '6.5s' },
];

const floatStyle = ({ x, y, delay, duration }) => ({
  insetInlineStart: x, insetBlockEnd: y, animationDelay: delay, animationDuration: duration,
});

export function DailyMessageModal({ message, onClose }) {
  return (
    <Modal onClose={onClose} ariaLabel="הודעה יומית" size="sm" className="cc-daily-modal">
      <div className="cc-daily-sea">
        <div className="cc-daily-floats" aria-hidden="true">
          {BUBBLES.map(b => (
            <span key={b.x} className="cc-daily-float cc-daily-bubble" style={{ ...floatStyle(b), width: b.size, height: b.size }} />
          ))}
          {HEARTS.map(h => (
            <span key={h.x} className="cc-daily-float cc-daily-heart" style={floatStyle(h)}>
              <Icon name="heart" size={h.size} />
            </span>
          ))}
        </div>
        <button type="button" className="icon-btn cc-daily-close" aria-label="סגור" onClick={onClose}>
          <Icon name="close" size={16} strokeWidth={2} />
        </button>
        <p className="cc-daily-text">{message}</p>
        <img className="cc-daily-mermaid" src={mermaidRedhead} alt="" aria-hidden="true" />
      </div>
    </Modal>
  );
}

export function DailyMessageEditor({ onClose }) {
  const [info, setInfo] = useState(null);
  const [text, setText] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    let alive = true;
    commandCenterApi.getDailyMessage()
      .then(({ data }) => {
        if (!alive) return;
        setInfo(data);
        setText(data.text);
        setEnabled(data.enabled);
      })
      .catch((err) => {
        if (!alive) return;
        toast(err.response?.data?.error || 'שגיאה בטעינת ההודעה היומית', 'error');
        onClose();
      });
    return () => { alive = false; };
    // Loaded once per opening; onClose is re-created by the caller on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await commandCenterApi.saveDailyMessage({ text, enabled });
      toast('ההודעה נשמרה', 'success');
      onClose();
    } catch (err) {
      toast(err.response?.data?.error || 'שגיאה בשמירת ההודעה היומית', 'error');
      setSaving(false);
    }
  };

  return (
    <>
      <Modal onClose={onClose} title="הודעה יומית" size="sm" closeOnBackdrop={false}>
        <form onSubmit={save}>
          <div className="form-group">
            <label htmlFor="cc-daily-text">טקסט ההודעה</label>
            <textarea
              id="cc-daily-text"
              className="form-control"
              rows={5}
              maxLength={info?.max_length}
              disabled={!info}
              value={text}
              onChange={e => setText(e.target.value)}
            />
          </div>
          {info && (
            <>
              <label className="cc-check">
                <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
                הצג את ההודעה
              </label>
              <p className="cc-daily-status">
                {enabled
                  ? <>מוצגת פעם ביום ל-<bdi>{info.recipient}</bdi>.</>
                  : <>כבויה, ולכן לא מוצגת לאף אחד.</>}
              </p>
            </>
          )}
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
            <button type="button" className="btn btn-outline" disabled={!text.trim()} onClick={() => setPreviewing(true)}>
              תצוגה מקדימה
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving || !text.trim()}>
              {saving ? 'שומר…' : 'שמור'}
            </button>
          </div>
        </form>
      </Modal>
      {previewing && <DailyMessageModal message={text.trim()} onClose={() => setPreviewing(false)} />}
    </>
  );
}
